/**
 * @file ccg-carousel.js
 * @description 記事内カルーセル (RTE 貼付された .c-carousel) を後付け初期化。
 *   - AI 生成 HTML は class だけ書けば良い (本ファイルが arrows/dots/keyboard を注入)
 *   - 外部依存なし (vanilla JS、IntersectionObserver で active dot 同期)
 *   - DOMContentLoaded + MutationObserver で動的追加にも対応
 *
 * 想定 markup:
 *   <div class="c-carousel">
 *     <div class="c-carousel__track">
 *       <figure class="c-carousel__item">...</figure>
 *       <figure class="c-carousel__item">...</figure>
 *       ...
 *     </div>
 *   </div>
 *
 * 自動注入される子:
 *   .c-carousel__prev / .c-carousel__next (arrow buttons)
 *   .c-carousel__dots > button.c-carousel__dot (page indicators)
 */
(function () {
  'use strict';
  const INIT_ATTR = 'data-ccg-carousel-init';

  function initCarousel(root) {
    if (root.getAttribute(INIT_ATTR)) return;
    const track = root.querySelector('.c-carousel__track');
    if (!track) return;
    const items = Array.from(track.querySelectorAll('.c-carousel__item'));

    // モード判定: `c-carousel--hero` または data-mode="fade" でフェード切替、
    // それ以外は scroll-snap モード (記事内カルーセル)。
    const isFade = root.classList.contains('c-carousel--hero') || root.dataset.mode === 'fade';

    // Single item: arrows/dots 不要だが、fade モードでは 1 枚目を active にしないと
    // CSS の opacity:0 が解除されず見えなくなる。
    if (items.length < 2) {
      if (isFade && items[0]) items[0].classList.add('c-carousel__item--active');
      root.setAttribute(INIT_ATTR, 'skip');
      return;
    }

    let activeIdx = 0;
    if (isFade) {
      items[0].classList.add('c-carousel__item--active');
    }

    // Prev/Next arrows
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'c-carousel__nav c-carousel__prev';
    prev.setAttribute('aria-label', '前の画像');
    prev.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'c-carousel__nav c-carousel__next';
    next.setAttribute('aria-label', '次の画像');
    next.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>';

    // Dot indicators
    const dotsWrap = document.createElement('div');
    dotsWrap.className = 'c-carousel__dots';
    dotsWrap.setAttribute('role', 'tablist');
    items.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'c-carousel__dot';
      dot.setAttribute('aria-label', (i + 1) + ' / ' + items.length);
      dot.setAttribute('role', 'tab');
      dot.dataset.index = i;
      dotsWrap.appendChild(dot);
    });

    root.appendChild(prev);
    root.appendChild(next);
    root.appendChild(dotsWrap);

    // Navigate to specific item.
    //   fade モード: active class 付け替え (opacity transition で CSS フェード)。インデックスはループ。
    //   scroll モード: track.scrollTo で snap。インデックスは clamp (両端で止まる)。
    function scrollToIndex(i) {
      if (isFade) {
        const n = items.length;
        const target = ((i % n) + n) % n;
        items.forEach((it, idx) => it.classList.toggle('c-carousel__item--active', idx === target));
        activeIdx = target;
        syncState();
      } else {
        const clamped = Math.max(0, Math.min(i, items.length - 1));
        const item = items[clamped];
        if (!item) return;
        track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }
    }

    function currentIndex() {
      if (isFade) return activeIdx;
      const trackRect = track.getBoundingClientRect();
      let nearest = 0;
      let minDelta = Infinity;
      items.forEach((it, i) => {
        const r = it.getBoundingClientRect();
        const delta = Math.abs(r.left - trackRect.left);
        if (delta < minDelta) { minDelta = delta; nearest = i; }
      });
      return nearest;
    }

    prev.addEventListener('click', () => scrollToIndex(currentIndex() - 1));
    next.addEventListener('click', () => scrollToIndex(currentIndex() + 1));
    dotsWrap.addEventListener('click', (e) => {
      const t = e.target.closest('.c-carousel__dot');
      if (t) scrollToIndex(parseInt(t.dataset.index, 10));
    });

    // Update active dot + nav disabled state.
    //   fade モードはループするので nav 常に有効。
    //   scroll モードは両端で disabled。
    function syncState() {
      const idx = currentIndex();
      dotsWrap.querySelectorAll('.c-carousel__dot').forEach((d, i) => {
        d.classList.toggle('c-carousel__dot--active', i === idx);
        d.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
      if (isFade) {
        prev.disabled = false;
        next.disabled = false;
      } else {
        prev.disabled = idx <= 0;
        next.disabled = idx >= items.length - 1;
      }
    }
    syncState();

    if (!isFade) {
      let scrollTO = null;
      track.addEventListener('scroll', () => {
        if (scrollTO) cancelAnimationFrame(scrollTO);
        scrollTO = requestAnimationFrame(syncState);
      }, { passive: true });
    }

    // Keyboard support on track focus
    track.setAttribute('tabindex', '0');
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); scrollToIndex(currentIndex() - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); scrollToIndex(currentIndex() + 1); }
      if (e.key === 'Home')       { e.preventDefault(); scrollToIndex(0); }
      if (e.key === 'End')        { e.preventDefault(); scrollToIndex(items.length - 1); }
    });

    // Pointer drag swipe (fade モード専用: scroll-snap は native で OK なので不要)
    //   - PC: マウスドラッグ / タッチ画面ノート PC で 動作
    //   - SP/タブレット: touch も pointer event 経由で統一
    // 横方向 50px 以上 + 縦方向のほうが小さい → swipe 判定
    if (isFade) {
      let startX = 0;
      let startY = 0;
      let dragging = false;
      let didSwipe = false;
      const SWIPE_THRESHOLD = 50;
      const MOVE_NOISE = 8; // この距離を超えて動いたら「移動意図あり」とみなす
      root.addEventListener('pointerdown', (e) => {
        // マウス時は左クリックのみ受付け (右クリック等で誤動作させない)
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        // 画像の native drag や text selection を抑止
        e.preventDefault();
        startX = e.clientX;
        startY = e.clientY;
        dragging = true;
        didSwipe = false;
      });
      root.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (Math.abs(dx) > MOVE_NOISE && Math.abs(dx) > Math.abs(dy)) {
          didSwipe = true;
        }
      });
      root.addEventListener('pointerup', (e) => {
        if (!dragging) return;
        dragging = false;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (Math.abs(dx) < SWIPE_THRESHOLD) return;
        if (Math.abs(dy) > Math.abs(dx)) return; // 縦移動が大きいときは無視
        if (dx < 0) scrollToIndex(currentIndex() + 1);
        else scrollToIndex(currentIndex() - 1);
        // 手動操作したので autoplay を止める
        root.dispatchEvent(new CustomEvent('ccg-carousel-manual'));
      });
      root.addEventListener('pointercancel', () => { dragging = false; didSwipe = false; });
      root.addEventListener('pointerleave', () => { dragging = false; });
      // swipe (= 移動意図あり) で発火する click を suppress
      // capture フェーズで child <a> より先に止める
      root.addEventListener('click', (e) => {
        if (didSwipe) {
          e.preventDefault();
          e.stopPropagation();
          didSwipe = false;
        }
      }, true);

      // Mac の 2 本指スクロール (touchpad horizontal scroll) や マウスホイール横は
      // wheel event で来るので別途検知。 deltaX を蓄積して閾値超えたら slide 移動。
      let wheelAccumX = 0;
      let wheelTimer = null;
      const WHEEL_THRESHOLD = 60;
      root.addEventListener('wheel', (e) => {
        // 横方向意図 (|deltaX| > |deltaY|) のみ受付け、 縦 scroll は通常通り
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        wheelAccumX += e.deltaX;
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => {
          if (Math.abs(wheelAccumX) >= WHEEL_THRESHOLD) {
            if (wheelAccumX > 0) scrollToIndex(currentIndex() + 1);
            else scrollToIndex(currentIndex() - 1);
            root.dispatchEvent(new CustomEvent('ccg-carousel-manual'));
          }
          wheelAccumX = 0;
        }, 80);
      }, { passive: false });
    }

    // Auto-rotate (オプション): data-ccg-autoplay="3000" 等 ms 指定で有効
    //   - hover / focus 中は一時停止
    //   - prefers-reduced-motion 配慮 (停止)
    //   - 最後 → 最初へループ
    const autoplayMs = parseInt(root.getAttribute('data-ccg-autoplay') || '0', 10);
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (autoplayMs > 0 && !reduceMotion) {
      let timer = null;
      let paused = false;
      const advance = () => {
        if (paused) return;
        const idx = currentIndex();
        const nextIdx = idx >= items.length - 1 ? 0 : idx + 1;
        scrollToIndex(nextIdx);
      };
      const start = () => {
        if (timer) return;
        timer = setInterval(advance, autoplayMs);
      };
      const stop = () => {
        if (timer) { clearInterval(timer); timer = null; }
      };
      const pause = () => { paused = true; };
      const resume = () => { paused = false; };
      root.addEventListener('pointerenter', pause);
      root.addEventListener('pointerleave', resume);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', resume);
      // ページが背景に行ったら停止 (リソース節約)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop(); else start();
      });
      // ユーザーが手動 swipe したら autoplay 終了 (主導権を譲る)
      root.addEventListener('ccg-carousel-manual', stop);
      start();
    }

    root.setAttribute(INIT_ATTR, 'true');
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.c-carousel:not([' + INIT_ATTR + '])').forEach(initCarousel);
  }

  // Initial pass
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Re-scan when DOM changes (Shopify Theme Editor inline edits, Alpine-driven sections, etc.)
  const observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.addedNodes && m.addedNodes.length) {
        initAll();
        break;
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Expose for manual re-init (e.g., after Ajax content load)
  window.CCG = window.CCG || {};
  window.CCG.initCarousels = initAll;
})();
